import {
  type DrillProgress,
  snaps,
  stageOf,
} from "../handwriting/xiezi-stages.js";
import type { HandoverCount } from "./lianxi-handover.js";

/**
 * What keeps a reader's place in an Exercise 练习 outside the page.
 *
 * `lianxi-xiezi.ts` tells a keeper each pass and adopts what it answers, and
 * `lianxi-recorder.ts` is the one keeper, posting to the `/user/lianxi/`
 * handler. The public page has none, and keeps nothing.
 */
export interface ExerciseKeeper {
  /**
   * Whether the finish has handed the Exercise's material to 复习, as far as
   * the keeper has heard. The line over a finished Exercise says so.
   */
  readonly handedOver: boolean;

  /** Whether the reader holds no Pro 专业, which the finish line names. */
  readonly free?: boolean | undefined;

  /** How many cards the hand-over put in and when the first is due, once heard. */
  readonly handoverCount?: HandoverCount | undefined;

  /**
   * A pass done on a character in a stage that snaps.
   * @param character - The character.
   * @param from - Where the character was when the pass began.
   * @returns Every character's place as kept, in the Exercise's order, or
   * nothing where the keeper could not say.
   */
  passDone(
    character: string,
    from: DrillProgress,
  ): Promise<readonly DrillProgress[] | undefined>;

  /** The Exercise started again from the first stage. */
  again(): Promise<void>;
}

/**
 * Ask the keeper about a pass that has ended, where the pass is one it keeps.
 *
 * A pass in a stage that snaps is the keeper's to keep. A pass in the free
 * stage was marked by the marker, which is where a keeper keeps it, so nothing
 * is asked.
 * @param keeper - The keeper, where there is one.
 * @param character - The character the pass was on.
 * @param before - Where the character was when the pass began.
 * @returns The keeper's answer, or nothing where nothing was asked.
 */
export function keptAfterPass(
  keeper: ExerciseKeeper | undefined,
  character: string,
  before: DrillProgress,
): Promise<readonly DrillProgress[] | undefined> | undefined {
  const stage = stageOf(before);

  return keeper !== undefined && stage !== undefined && snaps(stage)
    ? keeper.passDone(character, before)
    : undefined;
}

/**
 * What the round does once a pass has ended and the keeper has answered.
 *
 * The places the keeper answered are adopted over the page's own arithmetic,
 * and the page's own stands where nothing was asked or nothing came back.
 * The page's own is settled at once where nothing was asked, which is what
 * keeps the public page moving between characters with no turn of the clock.
 * @param kept - The keeper's answer, where anything was asked.
 * @param own - Settle the page's own progress.
 * @param adopt - Adopt the places the keeper answered.
 */
export function settleAfterPass(
  kept: Promise<readonly DrillProgress[] | undefined> | undefined,
  own: () => void,
  adopt: (places: readonly DrillProgress[]) => void,
): void {
  if (kept === undefined) {
    own();

    return;
  }

  void kept.then((places) => {
    if (places === undefined) {
      own();
    } else {
      adopt(places);
    }
  });
}

/**
 * The next character in the round with stages left, or nothing once every
 * character is finished.
 *
 * The characters are taken in turn, each unfinished one once, round and
 * round, so the search starts after the current one and comes back to it last.
 * @param progress - Where the reader is with each character.
 * @param current - Which character is in the square.
 * @param finished - Whether a progress is finished.
 * @returns The index of the next character, or nothing.
 */
export function nextUnfinished(
  progress: readonly DrillProgress[],
  current: number,
  finished: (progress: DrillProgress) => boolean,
): number | undefined {
  const count = progress.length;

  for (let step = 1; step <= count; step += 1) {
    const at = (current + step) % count;
    const held = progress[at];

    if (held !== undefined && !finished(held)) {
      return at;
    }
  }

  return undefined;
}
